import { Plus, Package } from 'lucide-react'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ProductListItem } from './ProductListItem'
import { ProductBrandEditor, type ProductFormData } from './ProductBrandEditor'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type Product from '@/types/serializers/Product'

interface ProductsSettingsCardProps {
  products: Product[] | null
  can_manage: boolean
  can_create: boolean
  max_products: number | null
}

const EMPTY_FORM: ProductFormData = {
  name: '',
  header_name: '',
  primary_color: '',
  accent_color: '',
  header_bg_color: '',
  header_text_color: '',
  active: true,
}

const NEW = 'new'
type Editing = Product | typeof NEW | null

export function ProductsSettingsCard({ products, can_manage, can_create, max_products }: ProductsSettingsCardProps) {
  const { withAccount } = useRouteParams()
  const [editing, setEditing] = useState<Editing>(null)
  const [formData, setFormData] = useState<ProductFormData>(EMPTY_FORM)

  if (!products) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Unable to load products.</p>
        </CardContent>
      </Card>
    )
  }

  const editingProduct = editing && editing !== NEW ? editing : null

  const openCreate = () => {
    setEditing(NEW)
    setFormData(EMPTY_FORM)
  }

  const select = (product: Product) => {
    if (editingProduct?.id === product.id) {
      setEditing(null)
      return
    }
    setEditing(product)
    setFormData({
      name: product.name,
      header_name: product.header_name ?? '',
      primary_color: product.primary_color ?? '',
      accent_color: product.accent_color ?? '',
      header_bg_color: product.header_bg_color ?? '',
      header_text_color: product.header_text_color ?? '',
      active: product.active,
    })
  }

  const payload = () => ({
    product: {
      name: formData.name.trim(),
      header_name: formData.header_name.trim() || null,
      primary_color: formData.primary_color || null,
      accent_color: formData.accent_color || null,
      header_bg_color: formData.header_bg_color || null,
      header_text_color: formData.header_text_color || null,
      active: formData.active,
    },
  })

  const handleSubmit = () => {
    if (!formData.name.trim()) return

    if (editingProduct) {
      router.patch(routes.products.update.path(withAccount({ id: editingProduct.slug })), payload(), {
        preserveScroll: true,
        onSuccess: () => setEditing(null),
      })
    } else {
      router.post(routes.products.create.path(withAccount()), payload(), {
        preserveScroll: true,
        onSuccess: () => setEditing(null),
      })
    }
  }

  const handleDelete = (product: Product) => {
    if (editingProduct?.id === product.id) setEditing(null)
    router.delete(routes.products.destroy.path(withAccount({ id: product.slug })), { preserveScroll: true })
  }

  const handleLogoUpload = (file: File) => {
    if (!editingProduct) return
    const data = new FormData()
    data.append('product[logo]', file)
    router.patch(routes.products.update.path(withAccount({ id: editingProduct.slug })), data, {
      forceFormData: true,
      preserveScroll: true,
    })
  }

  const list = (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="size-5" />
              Product & Brand
            </CardTitle>
            <CardDescription>
              Run multiple brands from one account — each with its own colours, logo, and identity. Add channels under Channels.
            </CardDescription>
          </div>
          {can_manage && (
            <div className="flex flex-col items-end gap-1">
              <Button onClick={openCreate} size="sm" disabled={!can_create} className="cursor-pointer">
                <Plus className="size-4 mr-1" />
                Add brand
              </Button>
              {!can_create && (
                <p className="text-xs text-muted-foreground">
                  {max_products === 1
                    ? 'Upgrade your plan to run more than one brand.'
                    : `Plan limit of ${max_products} brands reached — upgrade to add more.`}
                </p>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {products.map((product) => (
            <ProductListItem
              key={product.id}
              product={product}
              canManage={can_manage}
              selected={editingProduct?.id === product.id}
              onSelect={select}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )

  const detail = editing && (
    <ProductBrandEditor
      formData={formData}
      onFormChange={setFormData}
      onSubmit={handleSubmit}
      onCancel={() => setEditing(null)}
      isEditing={editing !== NEW}
      logoUrl={editingProduct?.logo_url}
      onLogoUpload={handleLogoUpload}
    />
  )

  return (
    <div className="space-y-6">
      {list}
      {detail}
    </div>
  )
}
